import Skeleton from 'react-loading-skeleton';
import {
  CheckboxButton,
  IconButton,
  NormalButton,
  ConfirmModal,
  AIECGViewer,
  ECGClient,
  TextInput,
  CloseIcon as closeIcon,
  TrashBoxRedIcon as deleteIcon,
} from '@/shared/ui';
import { TYPE_EVENT_OTHER } from '@/shared/config';
import { timezoneToOffset } from '@/shared/lib';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { STRIP_MANAGEMENT_TAB_ID } from '@/entities/report';
import useEditMoreInfo from './useEditMoreInfo';

const configChart = {
  isDisplayScrollbar: true,
  isEdit: true,
};

export type EditMoreInfoProps = {
  couldRemoveStrip?: boolean;
  data?: any;
  originalData?: any;
  handleSaveEditMoreInfo: (data: any) => void;
  handleCancelEditMoreInfo: () => void;
  handleClickCloseButton: () => void;
  ecgViewerConfig?: any;
  ecgDataMap: any;
  timezoneOffset?: any;
};

/**
 * Full editor for one AI captured strip, pairing a live ECG viewer with the title and technician comment fields. Renders a remove strip confirmation modal, a skeleton until ECG samples and the strip start epoch are ready, include-in-report and include-strip checkboxes, and a remove button gated on keeping at least one Min or Max HR strip.
 *
 * @param props - The edit strip props, carrying originalData, ecgDataMap, timezoneOffset and the close handler.
 * @returns The rendered strip editor with its ECG viewer, comment fields and footer actions.
 */
const EditMoreInfo = ({
  couldRemoveStrip: couldRemoveStripProp,
  data,
  originalData,
  handleSaveEditMoreInfo,
  handleCancelEditMoreInfo,
  handleClickCloseButton,
  ecgViewerConfig,
  ecgDataMap,
  timezoneOffset,
}: EditMoreInfoProps) => {
  const {
    couldRemoveStrip,
    state,
    setState,
    ecgObject,
    startStripEpoch,
    onClickSave,
    onClickCancel,
    onChange,
    onClickCheckbox,
    handleCaptureImages,
    getCaptureMode,
  } = useEditMoreInfo({
    couldRemoveStrip: couldRemoveStripProp,
    data,
    originalData,
    handleSaveEditMoreInfo,
    handleCancelEditMoreInfo,
    ecgViewerConfig,
    ecgDataMap,
    timezoneOffset,
  });

  return (
    <>
      <ConfirmModal
        isOpen={state.isOpenRemoveStripModal}
        onClickLeftButton={() => {
          setState({ isOpenRemoveStripModal: false });
        }}
        onClickRightButton={() => {
          setState({
            isRemoved: true,
            isOpenRemoveStripModal: false,
          });
        }}
        leftButtonName="Cancel"
        rightButtonName="Remove"
        title="Remove strip"
        isRedBtn
        isFilledRightBtn={false}
        question="Are you sure you want to remove this strip"
      />
      <div className="ai-strip-header">
        <div>EDIT STRIP</div>
        <IconButton
          isFilled
          iconComponent={<img src={closeIcon} alt="Close icon" />}
          onClick={handleClickCloseButton}
        />
      </div>
      <div className="ai-strip-content">
        <div className="start-time">
          <div>Start time:</div>
          <b>
            {originalData?.start
              ? convertToMomentJs(
                  originalData.start,
                  timezoneToOffset(originalData.timezone),
                )?.format('MM/DD/YYYY, HH:mm:ss')
              : ''}
          </b>
        </div>
        <div className="ecg-strip">
          {state.isLoading || !ecgObject?.dataSamples?.length || !startStripEpoch.current ? (
            <div id="ecg-viewer" className="ecg-viewer-container">
              <div className="ecg-viewer-skeleton-container">
                <div className="scroller-container">
                  <Skeleton height="100%" />
                </div>
                <div className="chart-container">
                  <Skeleton height="100%" />
                </div>
                <div className="menu-container">
                  <Skeleton height="100%" />
                </div>
              </div>
            </div>
          ) : (
            <ECGClient>
              <AIECGViewer
                idComponents={{
                  canvasBackground: 'cvBgEventStrip',
                  exportGridCanvas: 'expGridCvEventStrip',
                  canvasChart: 'cvChartEventStrip',
                  canvasObject: 'cvObjEventStrip',
                  canvasScroller: 'cvScrollerEventStrip',
                  contextMenu: 'contextMenuEventStrip',
                  tooltip: 'tooltipEventStrip',
                  tab: STRIP_MANAGEMENT_TAB_ID,
                }}
                ecg={ecgObject}
                ecgDataMap={ecgDataMap}
                configChart={configChart}
                isAppliedAI
                isDisableAIAssist
                isHideAddRemoveBeatMenu
                isHideFineGridMenu
                isHideMarkASampleMenu
                hasArrowButtonScroller={false}
                timeObject={{
                  startMoment: startStripEpoch.current,
                  timezoneOffset,
                }}
                aiChannel={state.aiChannel}
                captureMode={getCaptureMode()}
                handleCaptureImages={handleCaptureImages}
                hideTechnicianComments
                hideCaptureTitle
              />
            </ECGClient>
          )}
        </div>
      </div>
      <div className="ai-strip-sub-content">
        {originalData?.type === TYPE_EVENT_OTHER && (
          <div className="technician-comment mb3">
            <TextInput
              className="w-100 mt1 custom-techinician-comment-input"
              name="title"
              type="text"
              label="Title"
              placeholder="Enter title"
              value={state.title}
              onChange={onChange}
              disabled={state.isRemoved}
              maxLength={100}
              id="edit-strip-title"
            />
          </div>
        )}
        <div className="technician-comment">
          <TextInput
            className="w-100 mt1 custom-techinician-comment-input"
            name="technicianComments"
            type="textarea"
            label="Technician comment"
            placeholder="Enter comment"
            value={state.technicianComments}
            onChange={onChange}
            disabled={state.isRemoved}
            maxLength={520}
            id="edit-strip-technician-comment"
          />
        </div>
      </div>
      <div className="ai-strip-footer">
        <div className="left-part">
          <div className="checkbox-group">
            <CheckboxButton
              disabled={state.isRemoved}
              className="mr3"
              buttonName="Include in report"
              checked={state.isIncludedToReport}
              onClick={() => {
                onClickCheckbox('isIncludedToReport');
              }}
            />
            {state.isIncludedToReport && (
              <CheckboxButton
                disabled={state.isRemoved}
                className="mr3"
                buttonName="Include strip"
                checked={state.selected}
                onClick={() => {
                  onClickCheckbox('selected');
                }}
              />
            )}
          </div>
          {!state.isRemoved && (
            <IconButton
              className="delete-button"
              isIconWithText
              iconComponent={<img src={deleteIcon} alt="Delete icon" />}
              isFilled
              iconText="Remove strip"
              onClick={() => {
                setState({ isOpenRemoveStripModal: true });
              }}
              disabled={!couldRemoveStrip}
              tooltip={couldRemoveStrip ? '' : 'Must have at least one Min/Max HR'}
            />
          )}
        </div>
        <div className="right-part">
          <NormalButton className="mr3" buttonName="Cancel" onClick={onClickCancel} />
          <NormalButton isFilled buttonName="Save" onClick={onClickSave} />
        </div>
      </div>
    </>
  );
};

export default EditMoreInfo;
